import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, message, Spin, Table, Space, Popconfirm, Tag, Progress, Modal } from 'antd';
import { ArrowLeftOutlined, ExperimentOutlined, EyeOutlined, DeleteOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { createTask, getTasks, deleteTask, cancelTask, TaskResponse, shouldDisplayInList } from '@/utils/taskManager';
import { isProfileVerifyRequired } from '@/api/taskManagerApi';
import MolStarViewer from '@/components/results/protein/structure/MolStarViewer';
import { formatUTCToLocal } from '@/utils/timeUtils';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

export default function AntibodyStructurePage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const [resultModalVisible, setResultModalVisible] = useState(false);

  // 加载任务列表
  const loadTasks = async () => {
    setLoading(true);
    try {
      const taskList = await getTasks(0, 100, undefined, 'antibody_service');
      // 匿名提交时后端会将任务归属默认管理员，不能用 visitorId 等值筛选
      const filteredTasks = taskList.filter((task: TaskResponse) => 
        task.task_type === 'antibody_structure'
      );
      
      // 过滤掉已删除的任务
      const deletedTaskIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      const finalTasks = filteredTasks.filter(task => !deletedTaskIds.includes(task.id));

      // 仅展示长时任务
      const displayTasks = finalTasks.filter(shouldDisplayInList);
      
      setTasks(displayTasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
    } catch (error) {
      console.error('加载任务列表失败:', error);
      if (!isProfileVerifyRequired(error)) {
        message.error('加载任务列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 验证序列函数：检查是否只包含一条序列
  const validateSequence = (sequence: string, chainType: string): boolean => {
    const trimmed = sequence.trim();
    if (!trimmed) {
      message.error(`${chainType}序列不能为空`);
      return false;
    }
    
    // 统计FASTA ID的数量（以>开头的行）
    const fastaIds = trimmed.split('\n').filter(line => line.trim().startsWith('>'));
    
    if (fastaIds.length > 1) {
      message.error(`${chainType}只支持单条序列，检测到${fastaIds.length}条序列，请删除多余序列`);
      return false;
    }
    
    // 提取纯序列（去除FASTA ID）
    const pureSequence = trimmed.split('\n')
      .filter(line => line.trim() && !line.trim().startsWith('>'))
      .join('');
    
    if (!pureSequence) {
      message.error(`${chainType}序列为空，请输入有效的氨基酸序列`);
      return false;
    }
    
    return true;
  };

  // 提交预测任务
  const handleSubmit = async (values: any) => {
    // 验证重链序列
    if (!validateSequence(values.heavy_chain, '重链')) {
      return;
    }
    
    // 验证轻链序列
    if (!validateSequence(values.light_chain, '轻链')) {
      return;
    }

    setSubmitting(true);
    try {
      const taskData = {
        name: values.name || '抗体结构预测',
        description: `抗体结构预测 - ${values.name || 'Antibody'}`,
        service_name: 'antibody_service',
        task_type: 'antibody_structure',
        handler_name: 'antibody_structure_predict_json',
        parameters: {
          heavy_chain: values.heavy_chain.trim(),
          light_chain: values.light_chain.trim(),
          name: values.name || 'Antibody'
        },
        priority: 1,
        timeout: 1800,
        max_retries: 2
      };

      await createTask(taskData);
      message.success('任务已提交，正在处理中...');
      form.resetFields();
      loadTasks();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || '提交任务失败';
      console.error('提交任务失败:', error);
      message.error(`提交任务失败: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  // 删除任务 - 修改为本地删除
  const handleDelete = async (taskId: string) => {
    try {
      // 从localStorage中删除任务ID
      const deletedTasks = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      if (!deletedTasks.includes(taskId)) {
        deletedTasks.push(taskId);
        localStorage.setItem('deletedTaskIds', JSON.stringify(deletedTasks));
      }
      
      // 从当前显示的任务列表中移除
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      
      message.success('任务已删除');
    } catch (error) {
      console.error('删除任务失败:', error);
      message.error('删除任务失败');
    }
  };

  const handleCancel = async (taskId: string) => {
    try {
      await cancelTask(taskId);
      message.success('任务已取消');
      loadTasks();
    } catch (error) {
      console.error('取消任务失败:', error);
      message.error('取消任务失败');
    }
  };

  const handleViewResult = (task: TaskResponse) => {
    if (task.result) {
      // 处理统一格式数据：从results[0].result中提取实际数据
      let structureData = task.result;
      if (task.result.results && Array.isArray(task.result.results) && task.result.results.length > 0) {
        structureData = task.result.results[0].result;
      }
      
      setSelectedResult({
        title: `${task.name} - 预测结果`,
        data: structureData,
        taskResult: task
      });
      setResultModalVisible(true);
    } else {
      message.warning('暂无结果数据');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      'pending': { color: 'blue', text: '等待中' },
      'running': { color: 'orange', text: '运行中' },
      'completed': { color: 'green', text: '已完成' },
      'failed': { color: 'red', text: '失败' },
      'cancelled': { color: 'gray', text: '已取消' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number, record: TaskResponse) => {
        if (record.status === 'completed') return <Progress percent={100} size="small" />;
        if (record.status === 'failed' || record.status === 'cancelled') return <Progress percent={0} status="exception" size="small" />;
        return <Progress percent={progress || 0} size="small" />;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => formatUTCToLocal(time),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: TaskResponse) => (
        <Space size="middle">
          {record.status === 'completed' && (
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              onClick={() => handleViewResult(record)}
            >
              查看结果
            </Button>
          )}
          {(record.status === 'pending' || record.status === 'running') && (
            <Button 
              type="link" 
              onClick={() => handleCancel(record.id)}
            >
              取消
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个任务吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 5000);
    return () => clearInterval(interval);
  }, []);

  return (

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        <div style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/antibody')}
            style={{ marginBottom: 16 }}
          >
            返回抗体分析
          </Button>
          
          <Typography>
            <Title level={2}>抗体结构预测</Title>
            <Paragraph>
              基于重链和轻链序列预测抗体的三维结构，支持抗体、纳米抗体和TCR结构预测。
            </Paragraph>
          </Typography>
        </div>

        <Card>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="name"
              label="抗体名称"
              initialValue="抗体"
            >
              <Input placeholder="输入抗体名称" />
            </Form.Item>
            <Form.Item
              name="heavy_chain"
              label="重链序列"
              rules={[{ required: true, message: '请输入重链序列' }]}
            >
              <TextArea 
                rows={6} 
                placeholder="输入抗体重链氨基酸序列，例如：EVQLVESGGGLVQPGGSLRLSCAASGFTFS..."
              />
            </Form.Item>
            <Form.Item
              name="light_chain"
              label="轻链序列"
              rules={[{ required: true, message: '请输入轻链序列' }]}
            >
              <TextArea 
                rows={6} 
                placeholder="输入抗体轻链氨基酸序列，例如：DIQMTQSPSSLSASVGDRVTITC..."
              />
            </Form.Item>
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={submitting}
                icon={<ExperimentOutlined />}
              >
                预测抗体结构
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card 
          title="任务列表" 
          style={{ marginTop: 24 }}
          extra={
            <Button 
              size="small" 
              onClick={loadTasks}
              loading={loading}
              icon={<SyncOutlined />}
            >
              刷新任务
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={tasks}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 个任务`,
            }}
          />
        </Card>

        <Modal
          title="抗体结构预测结果"
          open={resultModalVisible}
          onCancel={() => {
            setResultModalVisible(false);
            setSelectedResult(null);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setResultModalVisible(false);
              setSelectedResult(null);
            }}>
              关闭
            </Button>,
            selectedResult?.data?.pdb_data && (
              <Button 
                key="download"
                type="primary"
                onClick={() => {
                  const pdbData = selectedResult.data.pdb_data || selectedResult.data.pdb_content;
                  if (pdbData) {
                    const blob = new Blob([pdbData], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'antibody_structure.pdb';
                    a.click();
                    URL.revokeObjectURL(url);
                  }
                }}
              >
                下载PDB
              </Button>
            )
          ]}
          width={1200}
          centered
          bodyStyle={{ padding: 0, height: '80vh', overflow: 'hidden' }}
        >
          {selectedResult && (
            <div style={{ 
              width: '100%', 
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <MolStarViewer
                pdbData={selectedResult.data?.pdb_data || selectedResult.data?.pdb_content}
                title={selectedResult.title}
                height="auto"
              />
            </div>
          )}
        </Modal>
      </div>

  );
}